// Ships failure events to Tokyo (POST /api/client-events/): failures only, in batches about every
// 5 minutes, each event with its own time. Identical events in a batch collapse into one entry
// with a count. Signed out, only the anonymous events go; the rest wait for a sign-in (Tokyo
// treats an expired token as no user and would drop them). Delivery problems are never reported
// as events themselves.
import { REGISTRY } from './clientEventSchema';

const MAX_ENTRIES = 100;
const MAX_BATCH = 50;
const MAX_BODY_BYTES = 15000;
const MAX_COUNT = 10000;
const REQUEST_TIMEOUT_MS = 10000;

const keyOf = event => event.name + JSON.stringify(Object.entries(event.fields).sort());

const utf8Length = text => {
  let bytes = 0;
  for (const character of text) {
    const code = character.codePointAt(0);
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
};

export const createShipper = ({
  send, getAuthorization, getClient, now = () => new Date(), requestTimeoutMs = REQUEST_TIMEOUT_MS,
}) => {
  const entries = new Map();
  let inFlight = null;
  let activeRequest = null;
  // Bumped when a drain's deadline passes: every flush started before then stops.
  let cancellations = 0;

  const add = event => {
    const key = keyOf(event);
    const entry = entries.get(key);
    if (entry) {
      entry.count += 1;
      if (event.occurred_at > entry.last_at) entry.last_at = event.occurred_at;
    } else if (entries.size < MAX_ENTRIES) {
      entries.set(key, {
        name: event.name,
        occurred_at: event.occurred_at,
        last_at: event.occurred_at,
        count: 1,
        fields: event.fields,
      });
    }
  };

  // Sends one batch: 'sent' when entries were delivered or refused for good, 'kept' when they
  // stay for a retry, 'nothing' when nothing could go.
  const ship = async (stopped = () => false) => {
    const cancellationsAtStart = cancellations;
    const authorization = getAuthorization() || '';
    let batch = [...entries.entries()]
      .filter(([, entry]) => authorization || REGISTRY[entry.name].anonymous)
      .slice(0, MAX_BATCH)
      // Tokyo accepts counts up to MAX_COUNT; the rest stay queued.
      .map(([key, entry]) => [key, { ...entry, count: Math.min(entry.count, MAX_COUNT) }]);
    if (!batch.length) return 'nothing';

    let client;
    try {
      client = await getClient();
    } catch (error) {
      return 'kept'; // The desktop app's details are unavailable (closing): retry next flush.
    }
    if (stopped() || cancellations !== cancellationsAtStart) return 'kept';
    const bodyFor = items => JSON.stringify({
      schema: 1, client, sent_at: now().toISOString(), events: items.map(([, entry]) => entry),
    });
    let body = bodyFor(batch);
    while (utf8Length(body) > MAX_BODY_BYTES && batch.length > 1) {
      batch = batch.slice(0, Math.ceil(batch.length / 2));
      body = bodyFor(batch);
    }

    const controller = new AbortController();
    activeRequest = controller;
    let timer;
    let status;
    try {
      ({ status } = await Promise.race([
        send({ body, authorization, signal: controller.signal }),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error('timeout'));
          }, requestTimeoutMs);
        }),
      ]));
    } catch (error) {
      return 'kept'; // Unreachable or too slow: keep the batch for the next flush.
    } finally {
      clearTimeout(timer);
      activeRequest = null;
    }
    // Accepted, or refused for good (malformed, too large): either way those occurrences go.
    // Occurrences added while the request was in flight stay.
    if (status !== 202 && status !== 400 && status !== 413) return 'kept';
    for (const [key, sentEntry] of batch) {
      const entry = entries.get(key);
      if (!entry) continue;
      entry.count -= sentEntry.count;
      if (entry.count <= 0) {
        entries.delete(key);
      } else {
        entry.occurred_at = entry.last_at;
      }
    }
    return 'sent';
  };

  // One flush at a time. A final flush (drain) sends batch after batch until the queue is
  // empty, nothing more can go, or the deadline passes; at the deadline the request in flight is
  // abandoned (its entries stay queued), whoever started it.
  const flush = ({ drain = false, deadlineMs = 2000 } = {}) => {
    if (inFlight && !drain) return inFlight;

    const deadline = Date.now() + deadlineMs;
    let expired = false;
    const timer = drain ? setTimeout(() => {
      expired = true;
      cancellations += 1;
      if (activeRequest) activeRequest.abort();
    }, deadlineMs) : null;

    const isExpired = () => expired;
    const run = async () => {
      if (inFlight) await inFlight;
      while (!isExpired() && entries.size && Date.now() < deadline) {
        if (await ship(isExpired) !== 'sent') break;
      }
    };
    const flushing = (drain ? run() : ship()).finally(() => {
      clearTimeout(timer);
      if (inFlight === flushing) inFlight = null;
    });
    inFlight = flushing;
    return flushing;
  };

  return { add, flush, size: () => entries.size };
};

const DESKTOP_CHANNELS = { releases: 'stable', beta: 'beta', alpha: 'alpha' };
const DESKTOP_PLATFORMS = { win32: 'windows', darwin: 'mac', linux: 'linux' };

// Who is reporting: the contract's app/version/platform/channel.
export const clientInfo = async ({ isElectron, api, webVersion, hostname }) => {
  if (isElectron) {
    const channel = await api.appChannel();
    return {
      app: 'denver',
      version: api.appVersion(),
      platform: api.isRPi ? 'rpi' : DESKTOP_PLATFORMS[api.platform()] || 'linux',
      channel: DESKTOP_CHANNELS[channel] || 'devel',
    };
  }
  const stage = (hostname.match(/-(alpha|beta)\b/) || [])[1];
  return { app: 'denver', version: webVersion, platform: 'web', channel: stage || 'stable' };
};

const FIVE_MINUTES = 5 * 60 * 1000;

// Wires the shipper to the page: Tokyo's base URL and bearer from axios, a fetch that never goes
// through axios's interceptors (they log users out on errors), a flush every 5 minutes, when the
// page is hidden for good, on logout and when the desktop app closes.
export const startShipping = ({ axios, addEventSink, buildEvent, webVersion, isSignedIn }) => {
  let client = null;
  const shipper = createShipper({
    send: async ({ body, authorization, signal }) => {
      const base = axios.defaults.baseURL || '/';
      const response = await fetch(base.replace(/\/?$/, '/') + 'api/client-events/', {
        method: 'POST',
        keepalive: true,
        signal,
        headers: { 'Content-Type': 'application/json', ...(authorization ? { Authorization: authorization } : {}) },
        body,
      });
      return { status: response.status };
    },
    getAuthorization: () => (isSignedIn() ? axios.defaults.headers.common['Authorization'] : ''),
    // Looked up once; a failed lookup is forgotten so the next flush asks again.
    getClient: () => {
      client = client || clientInfo({
        isElectron: !!window.isElectron, api: window.api, webVersion, hostname: window.location.hostname,
      }).catch(error => {
        client = null;
        throw error;
      });
      return client;
    },
  });

  addEventSink(shipper.add);
  if (window.isElectron && window.api.onMainClientEvent) {
    window.api.onMainClientEvent(event => {
      const { event: built } = buildEvent(event && event.name, event && event.fields);
      if (built) shipper.add({ ...built, occurred_at: (event && event.occurred_at) || built.occurred_at });
    });
  }
  setInterval(shipper.flush, FIVE_MINUTES);
  window.addEventListener('pagehide', () => shipper.flush({ drain: true }));
  window.flushClientEvents = shipper.flush;
  return shipper;
};
